/** «5 минут»: 10–15 cards of the active language, two big answers, then a short summary. */
import { useState } from 'react';
import { routes, useRouter } from '../state/router.tsx';
import { useAppData, useLoadedData } from '../state/app-data.tsx';
import { useCourseCards, useToday } from '../state/selectors.ts';
import { Rating } from '../core/fsrs.ts';
import { buildQuickSession } from '../core/review-queue.ts';
import { xpForReview } from '../core/progress.ts';
import type { Card } from '../data/api.ts';
import { RoboCat, type RoboCatState } from '../ui/RoboCat.tsx';
import { Icon } from '../ui/Icon.tsx';
import { languageInfo } from '../ui/course.ts';
import { canSpeak, speak } from '../ui/speech.ts';
import { playFeedback } from '../ui/feedback.ts';
import { LessonComplete } from './lesson/LessonComplete.tsx';
import './lesson/lesson.css';
import './quick.css';

interface Tally {
  remembered: number;
  forgotten: number;
  xp: number;
}

export function QuickScreen() {
  const { navigate } = useRouter();
  const { newSeenToday, newSeenDay } = useLoadedData();
  const { reviewCard, checkAchievements } = useAppData();
  const cards = useCourseCards();
  const today = useToday();
  // The session is fixed when it starts: reviewing reorders the queue underneath it.
  const [session] = useState<Card[]>(() => buildQuickSession(cards, new Date(), newSeenDay === today ? newSeenToday : {}));
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [tally, setTally] = useState<Tally>({ remembered: 0, forgotten: 0, xp: 0 });
  const [cat, setCat] = useState<RoboCatState>('idle');

  const close = () => navigate(routes.home);
  const card = session[index];

  if (session.length === 0) {
    return (
      <div className="quick quick--empty">
        <RoboCat size={110} state="idle" alive />
        <h1 className="t-card">Пока нечего повторять</h1>
        <p className="t-meta">Пройдите главу «Слова» в уроке — слова появятся здесь.</p>
        <button type="button" className="btn btn--primary btn--block" onClick={close}>
          На главную
        </button>
      </div>
    );
  }

  if (!card) {
    const total = tally.remembered + tally.forgotten;
    return (
      <LessonComplete
        title="5 минут — готово!"
        subtitle={`Помню ${tally.remembered} из ${total}`}
        xp={tally.xp}
        accuracy={total ? tally.remembered / total : 0}
        newWords={total}
        continueLabel="На главную"
        onContinue={close}
      />
    );
  }

  const speechLang = languageInfo(card.lang).speechLang;

  const answer = (rating: Rating) => {
    const stateBefore = card.state;
    reviewCard(card, rating);
    const remembered = rating !== Rating.Again;
    playFeedback(remembered ? 'correct' : 'wrong');
    setCat(remembered ? 'happy' : 'sad');
    window.setTimeout(() => setCat('idle'), 700);
    setTally((t) => ({
      remembered: t.remembered + (remembered ? 1 : 0),
      forgotten: t.forgotten + (remembered ? 0 : 1),
      xp: t.xp + xpForReview(rating, stateBefore),
    }));
    setRevealed(false);
    if (index + 1 >= session.length) {
      playFeedback('complete');
      checkAchievements();
    }
    setIndex((i) => i + 1);
  };

  return (
    <div className="lesson quick">
      <header className="lesson__head">
        <button type="button" className="icon-btn" onClick={close} aria-label="Закрыть">
          <Icon name="close" size={26} />
        </button>
        <div
          className="progress lesson__progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={session.length}
          aria-valuenow={index}
          aria-label="Прогресс «5 минут»"
        >
          <div className="progress__fill" style={{ width: `${(index / session.length) * 100}%` }} />
        </div>
      </header>
      <p className="lesson__chapter">
        5 минут · {index + 1} из {session.length}
      </p>

      <section className="quick__card" aria-live="polite">
        <div className="quick__cat">
          <RoboCat size={52} state={cat} />
        </div>
        <p className="quick__word han" lang={speechLang}>
          {card.front.text}
        </p>
        {canSpeak() && (
          <button type="button" className="icon-btn" onClick={() => speak(card.front.text, speechLang)} aria-label="Произнести">
            <Icon name="speaker" />
          </button>
        )}
        {revealed && (
          <div className="quick__back">
            {card.front.pinyin && <p className="quick__pinyin">{card.front.pinyin}</p>}
            <p className="quick__ru">{card.back.ru}</p>
          </div>
        )}
      </section>

      <div className="quick__actions">
        {revealed ? (
          <>
            <button type="button" className="btn btn--secondary quick__btn" onClick={() => answer(Rating.Again)}>
              Не помню
            </button>
            <button type="button" className="btn btn--primary quick__btn" onClick={() => answer(Rating.Good)}>
              Помню
            </button>
          </>
        ) : (
          <button type="button" className="btn btn--ink btn--block" onClick={() => setRevealed(true)}>
            Показать ответ
          </button>
        )}
      </div>
    </div>
  );
}
